
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  KeyboardSensor,
  MouseSensor,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { sortableKeyboardCoordinates } from '@dnd-kit/sortable';
import { isInputElement } from '@/utils/keyboard-utils';

interface UseDragDropSensorsOptions {
  /** Distance required before drag starts (prevents accidental drags) */
  activationDistance?: number;
  /** Whether to enable keyboard navigation */
  enableKeyboard?: boolean;
  /**
   * Opt in to a touch-friendly sensor pair on coarse-pointer devices.
   *
   * PointerSensor handles touch through Pointer Events, but to do so it sets
   * `touch-action: none` on the activator, which kills scrolling anywhere the
   * whole card is draggable. Swapping in MouseSensor + a long-press
   * TouchSensor lets a swipe scroll and a press-and-hold drag. Off by default
   * because most callers (tables, widgets) drag by a small handle where the
   * scroll conflict doesn't arise.
   */
  enableTouch?: boolean;
}

// Coarse pointer rather than viewport width: a touch tablet at 900px needs
// the same treatment. Matches the matchMedia pattern in use-mobile.tsx.
function useIsCoarsePointer() {
  const [isCoarse, setIsCoarse] = useState(false);

  useEffect(() => {
    const mql = window.matchMedia('(pointer: coarse)');
    const onChange = () => setIsCoarse(mql.matches);
    onChange();
    mql.addEventListener('change', onChange);
    return () => mql.removeEventListener('change', onChange);
  }, []);

  return isCoarse;
}

/**
 * Custom hook that provides drag-and-drop sensors configured to ignore
 * keyboard events when user is typing in input fields.
 * 
 * This prevents conflicts between drag-and-drop keyboard shortcuts and
 * normal typing in text inputs.
 */
export function useDragDropSensors(options: UseDragDropSensorsOptions = {}) {
  const {
    activationDistance = 5,
    enableKeyboard = true,
    enableTouch = false
  } = options;

  const isCoarsePointer = useIsCoarsePointer();
  const useTouchSensors = enableTouch && isCoarsePointer;

  // Track whether we should disable keyboard sensor due to input focus
  const [isInputFocused, setIsInputFocused] = useState(false);

  // Monitor focus changes to dynamically enable/disable keyboard sensor
  useEffect(() => {
    const handleFocusIn = (e: FocusEvent) => {
      const isInput = isInputElement(e.target as Element);
      setIsInputFocused(isInput);
    };

    const handleFocusOut = (e: FocusEvent) => {
      // Check if the newly focused element is also an input
      const isInput = isInputElement(e.relatedTarget as Element);
      setIsInputFocused(isInput);
    };

    // Listen to focus events on the document
    document.addEventListener('focusin', handleFocusIn);
    document.addEventListener('focusout', handleFocusOut);

    // Initial check
    const isInput = isInputElement(document.activeElement);
    setIsInputFocused(isInput);

    return () => {
      document.removeEventListener('focusin', handleFocusIn);
      document.removeEventListener('focusout', handleFocusOut);
    };
  }, []);

  // Always create every sensor to avoid conditional hook calls; which ones
  // are handed to useSensors is decided below.
  const pointerSensor = useSensor(PointerSensor, {
    activationConstraint: {
      distance: activationDistance,
    },
  });

  const mouseSensor = useSensor(MouseSensor, {
    activationConstraint: {
      distance: activationDistance,
    },
  });

  // Press and hold to pick up; a plain swipe within the tolerance stays a
  // scroll.
  const touchSensor = useSensor(TouchSensor, {
    activationConstraint: {
      delay: 250,
      tolerance: 8,
    },
  });

    // Create keyboard sensor with complete input awareness
  const keyboardSensor = useSensor(KeyboardSensor, {
    coordinateGetter: useCallback((
      event: KeyboardEvent,
      args: Parameters<typeof sortableKeyboardCoordinates>[1]
    ) => {
      // If keyboard is disabled, don't handle the event
      if (!enableKeyboard) {
        return null;
      }
      
      // If input is focused (from our state tracking), don't handle the event
      if (isInputFocused) {
        return null;
      }
      
      // Double-check at the moment of the event as well
      const activeElement = document.activeElement;
      if (isInputElement(activeElement)) {
        return null;
      }
      
      // For additional safety, check if the event target is an input field
      const target = event.target as HTMLElement;
      if (isInputElement(target)) {
        return null;
      }
      
      // Only handle specific keys for drag operations (arrow keys, enter, escape)
      // Don't handle space, letters, numbers, or other typing keys
      const allowedKeys = ['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight', 'Enter', 'Escape', 'Tab'];
      if (!allowedKeys.includes(event.code) && !allowedKeys.includes(event.key)) {
        return null;
      }
      
      // Otherwise, use the default coordinate getter
      return sortableKeyboardCoordinates(event, args);
    }, [enableKeyboard, isInputFocused]),
  });

  // Temporarily disable keyboard sensor to debug spacebar issue
  // Only use pointer sensor to avoid keyboard event conflicts
  //
  // useSensors spreads its arguments into a useMemo dep array, so the slot
  // count has to stay constant across renders — unused slots pass null,
  // which it filters out.
  const sensors = useSensors(
    useTouchSensors ? null : pointerSensor,
    useTouchSensors ? mouseSensor : null,
    useTouchSensors ? touchSensor : null
    // keyboardSensor  // Disabled to fix spacebar issue
  );

  return useMemo(() => ({
    sensors,
    // Expose the current input focus state for custom use cases
    isInputFocused,
  }), [sensors, isInputFocused]);
}